import { openDB } from "idb";
import { set } from "idb-keyval";
import { useRef, useState, useCallback, useEffect } from "react";

export function useVersionedIndexedDBBackup(dbName, storeNames) {
  const fileHandleRef = useRef(null);
  const firefoxFileData = useRef(null);
  const saveTimeoutRef = useRef(null);
  const storeNamesRef = useRef(storeNames);
  storeNamesRef.current = storeNames;
  const dbNameRef = useRef(dbName);
  dbNameRef.current = dbName;

  const supportsFSAccess =
    "showOpenFilePicker" in window &&
    "showSaveFilePicker" in window;

  const MAX_VERSIONS = 10;
  const THROTTLE_MS = 2000;

  const ASKED_KEY = `${dbNameRef.current}-asked-local-backup`;

  const HANDLE_DB_NAME = `${dbNameRef.current}-backup-handle`;
  const HANDLE_STORE = "handles";

  const [hasBackupFileState, setHasBackupFileState] = useState(false);
  const [undoAvailableState, setUndoAvailableState] = useState(false);
  const [redoAvailableState, setRedoAvailableState] = useState(false);
  const [supportsFSAccessState, setSupportsFSAccessState] = useState(supportsFSAccess);

  useEffect(() => {
    refreshStatus();
  }, []);

  /* -------------------------------------------------- */
  /*  File Handle Persistence                          */
  /* -------------------------------------------------- */

  async function getHandleDB() {
    return openDB(HANDLE_DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(HANDLE_STORE)) {
          db.createObjectStore(HANDLE_STORE);
        }
      },
    });
  }

  async function chooseBackupFile(existing) {
    if (!supportsFSAccess) {
      return chooseBackupFileFirefox();
    }
    // Prompt user: create new or open existing
    let handle;
    if(existing){
      // Try to let user pick an existing file
      [handle] = await window.showOpenFilePicker({
        types: [
          {
            description: "JSON Files",
            accept: { "application/json": [".json"] },
          },
        ],
        excludeAcceptAllOption: false,
        multiple: false,
      });

      // Request read/write permission explicitly
      const permission = await handle.requestPermission({ mode: "readwrite" });

      if (permission !== "granted") {
        throw new Error("Write permission not granted.");
      }
    }else{
      handle = await window.showSaveFilePicker({
        suggestedName: `${dbNameRef.current}-history.json`,
        types: [
          {
            description: "JSON Files",
            accept: { "application/json": [".json"] },
          },
        ],
      });
    }

    console.log("BACKUP - User selected backup file:", handle);

    fileHandleRef.current = handle;

    const handleDB = await getHandleDB();
    await handleDB.put(HANDLE_STORE, handle, "backupFile");

    markAskedToBackup();

    await syncOnFileOpen({ mergeOnChoose: true });

    await refreshStatus();
  }

  async function chooseBackupFileFirefox() {
    return new Promise((resolve, reject) => {

      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".json";

      input.onchange = async () => {

        const file = input.files[0];
        if (!file) return reject();

        const text = await file.text();
        firefoxFileData.current = text;

        fileHandleRef.current = {
          firefox: true,
          name: file.name
        };

        await syncOnFileOpen({ mergeOnChoose: true });
        await refreshStatus();

        resolve();
      };

      input.click();
    });
  }

async function loadBackupFile() {
  const handleDB = await getHandleDB();
  const handle = await handleDB.get(HANDLE_STORE, "backupFile");

  console.log("BACKUP - Loaded backup file handle from IndexedDB:", handle);

  if (!handle) {
    fileHandleRef.current = null;
    await refreshStatus();
    return;
  }

  const permission =
    (await handle.queryPermission({ mode: "readwrite" })) ||
    (await handle.requestPermission({ mode: "readwrite" }));

  if (permission === "granted") {
    fileHandleRef.current = handle;
    await syncOnFileOpen({mergeOnChoose: false});
  } else {
    fileHandleRef.current = null;
  }

  await refreshStatus();
}

async function databaseHasContent() {
  const db = await openDB(dbNameRef.current);
  for (const storeName of storeNamesRef.current) {
    const count = await db.count(storeName);
    if (count > 0) return true;
  }
  return false;
}

async function loadLatestSnapshotIntoDB({ merge = false } = {}) {
  const historyFile = await readHistoryFile();
  if (!historyFile.versions.length) return;

  const latestIndex = historyFile.versions.length - 1;
  await restoreByIndex(latestIndex, merge);
}

async function syncOnFileOpen({ mergeOnChoose = false }) {
  const historyFile = await readHistoryFile();
  const dbHasData = await databaseHasContent();

  // CASE 1 — file empty → snapshot DB into file
  if (!historyFile.versions.length) {
    if (dbHasData) {
      await writeSnapshot();
    }
    return;
  }

  // CASE 2 — choosing file → merge
  if (mergeOnChoose) {
    await loadLatestSnapshotIntoDB({ merge: true });
    return;
  }

  // CASE 3 — reloading file → replace DB
  await loadLatestSnapshotIntoDB({ merge: false });
}

  /* -------------------------------------------------- */
  /*  Asked-To-Backup Tracking                         */
  /* -------------------------------------------------- */

  function hasAskedToBackup() {
    return localStorage.getItem(ASKED_KEY) === "true";
  }

  function markAskedToBackup() {
    localStorage.setItem(ASKED_KEY, "true");
  }

  /* -------------------------------------------------- */
  /*  History File Management                          */
  /* -------------------------------------------------- */

  function defaultHistory() {
    return {
      meta: {
        dbName: dbNameRef.current,
        created: Date.now(),
        currentIndex: -1,
      },
      versions: [],
    };
  }

  async function readHistoryFile() {

    if (fileHandleRef.current?.firefox) {

      try {
        return JSON.parse(firefoxFileData.current);
      } catch {
        return defaultHistory();
      }

    }

    try {
      const file = await fileHandleRef.current.getFile();
      const text = await file.text();
      return JSON.parse(text);

    } catch {
      return defaultHistory();
    }
  }

  async function writeHistoryFile(historyFile) {
    const writable = await fileHandleRef.current.createWritable();
    //await writable.write(JSON.stringify(historyFile, null, 2)); // pretty print for debugging
    await writable.write(JSON.stringify(historyFile));
    await writable.close();
  }

  async function writeHistoryFileFirefox(historyFile) {
    var historyFile = defaultHistory();

    // 2️⃣ Create snapshot from IndexedDB
    const snapshot = await createSnapshot();

    // 3️⃣ Create version entry
    const version = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      data: snapshot
    };

    // 4️⃣ Add to history
    historyFile.versions.push(version);
    historyFile.meta.currentIndex = historyFile.versions.length - 1;

    if (fileHandleRef.current?.firefox || !supportsFSAccess) {
      const blob = new Blob(
        [JSON.stringify(historyFile)],
        { type: "application/json" }
      );

      const url = URL.createObjectURL(blob);

      const now = new Date();

      const timestamp =
        now.getFullYear() + "-" +
        String(now.getMonth() + 1).padStart(2, "0") + "-" +
        String(now.getDate()).padStart(2, "0") + "_" +
        String(now.getHours()).padStart(2, "0") + "-" +
        String(now.getMinutes()).padStart(2, "0") + "-" +
        String(now.getSeconds()).padStart(2, "0");

      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${timestamp}.json`;
      a.click();

      URL.revokeObjectURL(url);
      return;
    }
  }

  async function createSnapshot() {
    const db = await openDB(dbNameRef.current);
    const historyFile = await readHistoryFile();

    if (!historyFile.blobs) historyFile.blobs = {};

    const snapshotData = {};

    for (const storeName of storeNamesRef.current) {
      const tx = db.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);

      const allRecords = await store.getAll();
      const processedRecords = [];

      for (const record of allRecords) {
        const copy = structuredClone(record);
        // Handle media blobs
        if (storeName === "media" && copy.file instanceof Blob) {
          const hash = await hashBlob(copy.file);
          if (!historyFile.blobs[hash]) {
            historyFile.blobs[hash] = {
              type: copy.file.type,
              data: await compressToArrayBuffer(copy.file)
            };
          }
          copy.fileHash = hash;
          delete copy.file;
        }
        processedRecords.push(copy);
      }
      snapshotData[storeName] = processedRecords;
      await tx.done;
    }
    return { snapshotData, blobs: historyFile.blobs };
  }

  // async function blobToBase64(blob) {
  //   const buffer = await blob.arrayBuffer();
  //   const bytes = new Uint8Array(buffer);

  //   let binary = "";
  //   for (let i = 0; i < bytes.length; i++) {
  //     binary += String.fromCharCode(bytes[i]);
  //   }

  //   return btoa(binary);
  // }

  async function compressBlob(blob) {
    const cs = new CompressionStream("gzip");
    const compressedStream = blob.stream().pipeThrough(cs);
    return await new Response(compressedStream).blob();
  }

  async function compressToArrayBuffer(blob) {
    const compressed = await compressBlob(blob);
    return await compressed.arrayBuffer();
  }

  // function base64ToBlob(base64, type) {
  //   const binary = atob(base64);
  //   const bytes = new Uint8Array(binary.length);

  //   for (let i = 0; i < binary.length; i++) {
  //     bytes[i] = binary.charCodeAt(i);
  //   }

  //   return new Blob([bytes], { type });
  // }

  async function decompressArrayBufferToBlob(arrayBuffer, mimeType = "application/octet-stream") {
    // Convert ArrayBuffer into a Blob so we can stream it
    const compressedBlob = new Blob([arrayBuffer]);

    // Create decompression stream
    const ds = new DecompressionStream("gzip");

    // Pipe through decompression
    const decompressedStream = compressedBlob.stream().pipeThrough(ds);

    // Convert back to Blob
    return await new Response(decompressedStream).blob().then(
      blob => new Blob([blob], { type: mimeType })
    );
  }

  async function hashBlob(blob) {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  }

  async function hashSnapshot(snapshot) {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(snapshot));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  }

  async function hashJSON(obj) {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(obj));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");
  }

  async function writeSnapshot() {
    if (!hasBackupFileState) return;

    const historyFile = await readHistoryFile();
    const { snapshotData, blobs } = await createSnapshot();
    const newHash = await hashSnapshot(snapshotData);

    console.log("BACKUP - New snapshot hash:", newHash, " Snapshot data:", snapshotData);

    const latest = historyFile.versions[historyFile.versions.length - 1];

    // 🔥 Skip duplicate snapshot
    if (latest && latest.hash === newHash) {
      console.log("BACKUP - Snapshot unchanged since last version, skipping backup write.");
      return;
    }

    if (historyFile.meta.currentIndex < historyFile.versions.length - 1) {
      historyFile.versions = historyFile.versions.slice(
        0,
        historyFile.meta.currentIndex + 1
      );
    }

    historyFile.versions.push({
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      hash: newHash, // 🔥 store hash
      data: snapshotData,
    });

    if (historyFile.versions.length > MAX_VERSIONS) {
      historyFile.versions = historyFile.versions.slice(-MAX_VERSIONS);
    }

    historyFile.meta.currentIndex = historyFile.versions.length - 1;
    historyFile.blobs = blobs;

    await garbageCollectBlobs(historyFile);
    await writeHistoryFile(historyFile);
    await refreshStatus();
  }

  function scheduleSnapshot() {
    console.log("BACKUP - scheduleSnapshot called, hasBackupFileState:", hasBackupFileState);
    if(!hasBackupFileState) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(writeSnapshot, THROTTLE_MS);
  }

  async function garbageCollectBlobs(historyFile) {
    if(!historyFile.versions?.length || !historyFile.blobs) return;
    const referenced = new Set();

    for (const version of historyFile.versions) {
      if (!version.data) continue;
      for (const storeName of Object.keys(version.data)) {
        if (storeName !== "media") continue;
        const items = Array.isArray(version.data[storeName]) ? version.data[storeName] : [];
        for (const item of items) {
          if (item.fileHash) {
            referenced.add(item.fileHash);
          }
        }
      }
    }

    for (const hash of Object.keys(historyFile.blobs)) {
      if (!referenced.has(hash)) {
        delete historyFile.blobs[hash];
      }
    }
  }

  async function restoreByIndex(index, merge = false) {
    const historyFile = await readHistoryFile();
    const version = historyFile.versions[index];
    if (!version) return;

    const db = await openDB(dbNameRef.current);

    for (const storeName of storeNamesRef.current) {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);

      const items = Array.isArray(version.data[storeName]) ? version.data[storeName] : [];

      if (!merge) {
        await store.clear();
        for (const item of items) {
          const copy = structuredClone(item);
          // Rehydrate media blobs
          if (storeName === "media" && copy.fileHash) {
            if (historyFile.blobs && historyFile.blobs[copy.fileHash]) {
              const blobData = historyFile.blobs[copy.fileHash];
              copy.file = await decompressArrayBufferToBlob(
                new Uint8Array(blobData.data),
                blobData.type
              );
            } else {
              copy.file = null;
            }
            delete copy.fileHash;
          }
          await store.put(copy);
        }
      } else {
        // Merge mode: only add items that do not exist
        for (const item of items) {
          const copy = structuredClone(item);
          // Rehydrate media blobs
          if (storeName === "media" && copy.fileHash) {
            if (historyFile.blobs && historyFile.blobs[copy.fileHash]) {
              const blobData = historyFile.blobs[copy.fileHash];
              copy.file = await decompressArrayBufferToBlob(
                new Uint8Array(blobData.data),
                blobData.type
              );
            } else {
              copy.file = null;
            }
            delete copy.fileHash;
          }
          const existing = await store.get(copy.id);
          if (!existing) {
            await store.put(copy);
          }
        }
      }

      await tx.done;
    }

    historyFile.meta.currentIndex = index;
    historyFile.meta.dbHash = version.hash;

    await writeHistoryFile(historyFile);
    await refreshStatus();
  }

  async function undo() {
    const historyFile = await readHistoryFile();
    const newIndex = historyFile.meta.currentIndex - 1;
    if (newIndex >= 0) {
      await restoreByIndex(newIndex);
    }
  }

  async function redo() {
    const historyFile = await readHistoryFile();
    const newIndex = historyFile.meta.currentIndex + 1;
    if (newIndex < historyFile.versions.length) {
      await restoreByIndex(newIndex);
    }
  }

  async function listVersions() {
    const historyFile = await readHistoryFile();
    return historyFile.versions.map((v, i) => ({
      id: v.id,
      timestamp: v.timestamp,
      index: i,
      isCurrent: i === historyFile.meta.currentIndex,
    }));
  }

  const refreshStatus = useCallback(async () => {
    const supportsFSAccess =
      "showOpenFilePicker" in window &&
      "showSaveFilePicker" in window;
      setSupportsFSAccessState(supportsFSAccess);
    if (!fileHandleRef.current || !supportsFSAccess) {
        setHasBackupFileState(false);
        setUndoAvailableState(false);
        setRedoAvailableState(false);
        return;
    }

    const permission = await fileHandleRef.current.queryPermission({
        mode: "readwrite",
    });

    const hasFile = permission === "granted";
    setHasBackupFileState(hasFile);

    if (!hasFile) {
        setUndoAvailableState(false);
        setRedoAvailableState(false);
        return;
    }

    const historyFile = await readHistoryFile();

    setUndoAvailableState(historyFile.meta.currentIndex > 0);
    setRedoAvailableState(
        historyFile.meta.currentIndex <
        historyFile.versions.length - 1
    );
  }, []);

  return {
    hasBackupFile: hasBackupFileState,
    undoAvailable: undoAvailableState,
    redoAvailable: redoAvailableState,
    supportsFSAccess: supportsFSAccessState,
    chooseBackupFile,
    loadBackupFile,
    scheduleSnapshot,
    writeHistoryFileFirefox,
    undo,
    redo,
    restoreByIndex,
    listVersions,
    hasAskedToBackup,
    markAskedToBackup,
  };
}